import { Badge } from "@/components/ui/badge";

const STATUS_STYLES = {
  active: "bg-green-100 text-green-800 border-green-200",
  certified: "bg-green-100 text-green-800 border-green-200",
  paid: "bg-green-100 text-green-800 border-green-200",
  present: "bg-green-100 text-green-800 border-green-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  up_to_date: "bg-green-100 text-green-800 border-green-200",
  on_track: "bg-green-100 text-green-800 border-green-200",
  reviewed: "bg-emerald-100 text-emerald-800 border-emerald-200",

  pending: "bg-amber-100 text-amber-800 border-amber-200",
  late: "bg-amber-100 text-amber-800 border-amber-200",
  excused: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  expiring_soon: "bg-orange-100 text-orange-800 border-orange-200",
  unpaid: "bg-gray-100 text-gray-700 border-gray-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
  due: "bg-amber-100 text-amber-800 border-amber-200",
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  assigned: "bg-blue-100 text-blue-800 border-blue-200",
  due_soon: "bg-amber-100 text-amber-800 border-amber-200",
  not_started: "bg-gray-100 text-gray-700 border-gray-200",
  inactive: "bg-gray-100 text-gray-700 border-gray-200",

  suspended: "bg-red-100 text-red-800 border-red-200",
  expired: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  refunded: "bg-blue-100 text-blue-800 border-blue-200",
  absent: "bg-red-100 text-red-800 border-red-200",
  needs_attention: "bg-red-100 text-red-800 border-red-200",
  overdue: "bg-red-100 text-red-800 border-red-200",

  puppy: "bg-accent text-accent-foreground border-transparent",
  beginner: "bg-secondary text-secondary-foreground border-transparent",
  intermediate: "bg-blue-100 text-blue-800 border-blue-200",
  advanced: "bg-primary text-primary-foreground border-transparent",

  open: "bg-green-100 text-green-800 border-green-200",
  full: "bg-amber-100 text-amber-800 border-amber-200",
  requested: "bg-blue-100 text-blue-800 border-blue-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  waitlisted: "bg-amber-100 text-amber-800 border-amber-200",
  attended: "bg-emerald-100 text-emerald-800 border-emerald-200",
  no_show: "bg-red-100 text-red-800 border-red-200",
  client_bookable: "bg-blue-100 text-blue-800 border-blue-200",
  internal: "bg-gray-100 text-gray-700 border-gray-200",
};

export function StatusBadge({ value }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const cls = STATUS_STYLES[value] || "bg-secondary text-secondary-foreground border-transparent";
  const label = String(value).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Badge variant="outline" className={`${cls} font-medium capitalize`} data-testid={`badge-${value}`}>
      {label}
    </Badge>
  );
}

const SEVERITY_STYLES = {
  critical: "bg-red-100 text-red-800 border-red-200",
  warning: "bg-amber-100 text-amber-800 border-amber-200",
  info: "bg-blue-100 text-blue-800 border-blue-200",
};

export function SeverityBadge({ value }) {
  const cls = SEVERITY_STYLES[value] || SEVERITY_STYLES.info;
  return (
    <Badge variant="outline" className={`${cls} font-medium capitalize`} data-testid={`severity-${value}`}>
      {value}
    </Badge>
  );
}

export const BEHAVIOUR_FLAGS = [
  { value: "needs_extra_attention", label: "Needs extra attention", tone: "red" },
  { value: "reactive", label: "Reactive", tone: "red" },
  { value: "nervous", label: "Nervous", tone: "amber" },
  { value: "strong_puller", label: "Strong puller", tone: "amber" },
  { value: "good_progress", label: "Good progress", tone: "green" },
  { value: "ready_for_next_level", label: "Ready for next level", tone: "green" },
];

const FLAG_TONE = {
  red: "bg-red-100 text-red-800 border-red-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  green: "bg-green-100 text-green-800 border-green-200",
};

export function FlagBadge({ value }) {
  const f = BEHAVIOUR_FLAGS.find((x) => x.value === value);
  if (!f) return null;
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${FLAG_TONE[f.tone]}`} data-testid={`flag-${value}`}>
      {f.label}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-semibold text-foreground">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return v; }
}

export function fmtDKK(n) {
  return new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 0 }).format(n || 0);
}
